<template>
  <styled-tags-input
    :add-only-from-autocomplete="isTermSetField"
    :autocomplete-items="possibleValues ?? []"
    :busy="isBusy || isWaitingForValues || loading"
    :model-value="currentTagValues"
    :disabled="disabled"
    :is-error="!isInputValid"
    class="document-metadata-tags-input"
    @tags-changed="handleTagsChanged"
  ></styled-tags-input>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { useToast } from '@/composables/useToast';
  import StyledTagsInput from '@/components/StyledTagsInput/StyledTagsInput.vue';
  import {
    DEFAULT_METADATA_VALUES_SEPARATOR,
    EDITABLE_TERM_SET_METADATA_FIELDS,
    EditableTermSetMetadataField,
  } from 'types';
  import {
    ReloadMetadataFunction,
    UpdateMetadataFunction,
    ValidationFunction,
  } from '../DocumentsMetadata/DocumentsMetadata.types';

  interface DocumentMetadataTagInputProps {
    disabled?: boolean;
    isWaitingForValues?: boolean;
    currentValues: string;
    possibleValues?: string[];
    metadataField: string;
    loading?: boolean;
    validationFunction?: ValidationFunction;
    getMetadataFunction?: ReloadMetadataFunction;
    updateMetadataFunction: UpdateMetadataFunction;
  }

  const props = withDefaults(defineProps<DocumentMetadataTagInputProps>(), {
    disabled: false,
    isWaitingForValues: false,
  });

  const emit = defineEmits<{
    (e: 'document-data-changed'): void;
  }>();

  const { createErrorToast } = useToast();

  const isBusy = ref(false);
  const isInputValid = ref(true);

  const currentTagValues = computed<string[]>(() =>
    props.currentValues === ''
      ? []
      : props.currentValues.split(DEFAULT_METADATA_VALUES_SEPARATOR),
  );

  const isTermSetField = computed<boolean>(() =>
    EDITABLE_TERM_SET_METADATA_FIELDS.includes(
      props.metadataField as EditableTermSetMetadataField,
    ),
  );

  function validateInput(): void {
    if (props.validationFunction) {
      isInputValid.value = props.validationFunction(props.currentValues);
    }
  }

  async function handleTagsChanged(tags: string[] | undefined) {
    if (!tags) return;
    try {
      isBusy.value = true;
      await props.updateMetadataFunction(props.metadataField, tags);
    } catch {
      createErrorToast();
    } finally {
      isBusy.value = false;
      validateInput();
      emit('document-data-changed');
    }
  }

  watch(
    () => [props.currentValues, props.validationFunction],
    () => {
      validateInput();
    },
    { immediate: true },
  );
</script>

<style lang="scss" scoped>
  .document-metadata-tags-input {
    width: 100%;

    :deep(.vue-tags-input) {
      width: 100%;
      max-width: none;
    }
  }
</style>
